import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useVehicleTypes } from '@/hooks/useVehicleTypes';
import { useUpdateVehicle, Vehicle } from '@/hooks/useVehicles';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface EditVehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle: Vehicle;
}

export const EditVehicleDialog = ({ open, onOpenChange, vehicle }: EditVehicleDialogProps) => {
  const { t, i18n } = useTranslation();
  const { vehicleTypes, getVehicleTypeName } = useVehicleTypes();
  const { updateVehicle, isUpdating } = useUpdateVehicle();

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };
  
  const [vehicleTypeId, setVehicleTypeId] = useState(vehicle.vehicle_type_id);
  const [plateNumber, setPlateNumber] = useState(vehicle.plate_number);
  const [vin, setVin] = useState(vehicle.vin || '');
  const [brand, setBrand] = useState(vehicle.brand || '');
  const [model, setModel] = useState(vehicle.model || '');
  const [year, setYear] = useState(vehicle.year?.toString() || '');
  const [isAvailable, setIsAvailable] = useState(vehicle.is_available);
  const [lastMaintenanceDate, setLastMaintenanceDate] = useState<Date | undefined>(
    vehicle.last_maintenance_date ? new Date(vehicle.last_maintenance_date) : undefined
  );
  const [nextMaintenanceDate, setNextMaintenanceDate] = useState<Date | undefined>(
    vehicle.next_maintenance_date ? new Date(vehicle.next_maintenance_date) : undefined
  );

  useEffect(() => {
    if (open) {
      setVehicleTypeId(vehicle.vehicle_type_id);
      setPlateNumber(vehicle.plate_number);
      setVin(vehicle.vin || '');
      setBrand(vehicle.brand || '');
      setModel(vehicle.model || '');
      setYear(vehicle.year?.toString() || '');
      setIsAvailable(vehicle.is_available);
      setLastMaintenanceDate(vehicle.last_maintenance_date ? new Date(vehicle.last_maintenance_date) : undefined);
      setNextMaintenanceDate(vehicle.next_maintenance_date ? new Date(vehicle.next_maintenance_date) : undefined);
    }
  }, [open, vehicle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!vehicleTypeId || !plateNumber) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      await updateVehicle({
        id: vehicle.id,
        vehicle_type_id: vehicleTypeId,
        plate_number: plateNumber,
        vin: vin || null,
        brand: brand || null,
        model: model || null,
        year: year ? parseInt(year) : null,
        is_available: isAvailable,
        last_maintenance_date: lastMaintenanceDate?.toISOString() || null,
        next_maintenance_date: nextMaintenanceDate?.toISOString() || null,
      });
      
      toast.success('Véhicule modifié avec succès');
      onOpenChange(false);
    } catch (error) {
      toast.error('Erreur lors de la modification du véhicule');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            {t('fleet.dialogs.editVehicle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type de véhicule */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.vehicleType')} {t('fleet.dialogs.required')}
            </Label>
            <Select value={vehicleTypeId} onValueChange={setVehicleTypeId}>
              <SelectTrigger className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                <SelectValue placeholder={t('fleet.dialogs.selectType')} />
              </SelectTrigger>
              <SelectContent>
                {vehicleTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {getVehicleTypeName(type.trailer_type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Plaque d'immatriculation */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('fleet.dialogs.plateNumber')} {t('fleet.dialogs.required')}
              </Label>
              {vehicle.registration_verified_at && (
                <span className="text-[10px] text-[#14A38B] font-medium">
                  🔒 {t('fleet.documentStatus.lockedField')}
                </span>
              )}
            </div>
            <Input
              value={plateNumber}
              onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
              placeholder="AA-123-BB"
              readOnly={!!vehicle.registration_verified_at}
              disabled={!!vehicle.registration_verified_at}
              className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans'] disabled:bg-[#EBF4F8] disabled:opacity-100"
            />
          </div>

          {/* Statut */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.status')}
            </Label>
            <Select value={isAvailable ? 'true' : 'false'} onValueChange={(v) => setIsAvailable(v === 'true')}>
              <SelectTrigger className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{t('fleet.dialogs.statusAvailable')}</SelectItem>
                <SelectItem value="false">{t('fleet.dialogs.statusOnMission')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* VIN */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('fleet.dialogs.vin')}
              </Label>
              {vehicle.registration_verified_at && (
                <span className="text-[10px] text-[#14A38B] font-medium">
                  🔒 {t('fleet.documentStatus.lockedField')}
                </span>
              )}
            </div>
            <Input
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              placeholder="1HGBH41JXMN109186"
              readOnly={!!vehicle.registration_verified_at}
              disabled={!!vehicle.registration_verified_at}
              className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans'] disabled:bg-[#EBF4F8] disabled:opacity-100"
            />
          </div>

          {/* Marque et Modèle */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('fleet.dialogs.brand')}
              </Label>
              <Input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="Renault"
                readOnly={!!vehicle.registration_verified_at}
                disabled={!!vehicle.registration_verified_at}
                className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans'] disabled:bg-[#EBF4F8] disabled:opacity-100"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('fleet.dialogs.model')}
              </Label>
              <Input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="Master"
                readOnly={!!vehicle.registration_verified_at}
                disabled={!!vehicle.registration_verified_at}
                className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans'] disabled:bg-[#EBF4F8] disabled:opacity-100"
              />
            </div>
          </div>

          {/* Année */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.year')}
            </Label>
            <Input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="2023"
              min="1900"
              max={new Date().getFullYear() + 1}
              readOnly={!!vehicle.registration_verified_at}
              disabled={!!vehicle.registration_verified_at}
              className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans'] disabled:bg-[#EBF4F8] disabled:opacity-100"
            />
          </div>

          {/* Dates d'entretien */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('fleet.dialogs.lastMaintenance')}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal border-[#DBE2EA] hover:bg-[#EBF4F8] font-['Funnel_Sans']",
                      !lastMaintenanceDate && "text-[#87ADD4]"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#5A6B7A]" />
                    {lastMaintenanceDate ? format(lastMaintenanceDate, 'dd MMM yyyy', { locale: getDateLocale() }) : t('fleet.dialogs.select')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={lastMaintenanceDate}
                    onSelect={setLastMaintenanceDate}
                    disabled={(date) => date > new Date()}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('fleet.dialogs.nextMaintenance')}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal border-[#DBE2EA] hover:bg-[#EBF4F8] font-['Funnel_Sans']",
                      !nextMaintenanceDate && "text-[#87ADD4]"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#5A6B7A]" />
                    {nextMaintenanceDate ? format(nextMaintenanceDate, 'dd MMM yyyy', { locale: getDateLocale() }) : t('fleet.dialogs.select')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={nextMaintenanceDate}
                    onSelect={setNextMaintenanceDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#DBE2EA] text-[#5A6B7A] hover:bg-[#EBF4F8] font-['Funnel_Sans']"
            >
              {t('fleet.dialogs.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isUpdating}
              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg transition-colors duration-200"
            >
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('fleet.dialogs.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};